import ErrorBoundary from "@client/components/ErrorBoundary";
import FuzionLoader from "@client/components/FuzionLoader";
import { PersonModel, QueryItemsResult } from "@core/models";
import ErrorIcon from "@mui/icons-material/PriorityHigh";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Text from "@mui/material/Typography";
import {
    createContext,
    PropsWithChildren,
    Suspense,
    use,
    useContext,
    useMemo,
    useState
} from "react";
import StatusLabel from "../components/StatusLabel";
import { AuthHelper, exec, Logger } from "../helpers";

type AuthContextState = {
    state?: "authenticated" | "unauthenticated" | "error";
    token?: string;
    userData?: PersonModel;
}
const AuthContext = createContext({
    state: {} as AuthContextState,
});
type AuthContextProviderProps = PropsWithChildren<{}>;
export default function AuthContextProvider({ children }: AuthContextProviderProps) {
    const [state, setState] = useState<AuthContextState>({
        state: "unauthenticated",
        token: undefined,
        userData: undefined
    });

    const query = useMemo(async (): Promise<QueryResult> => {
        const token = await AuthHelper.authenticate().catch(err => {
            Logger.error("Authentication Error!", err);
            updateState({ state: "error" });
            throw err
        });
        const userData = await exec<QueryItemsResult>("QueryDatasource", {
            name: "GetPersonBySubjectId",
            context: {
                SubjectId: AuthHelper.getUserData(token)?.sub
            }
        }, { accessToken: token }).catch(err => {
            Logger.error("Failed to load user data!", err);
            return { count: 0, items: [] };
        });
        return {
            token,
            userData: userData.count > 0 ? userData.items[0] as PersonModel : undefined
        };
    }, []);

    return <AuthContext.Provider value={{ state }}>
        <Suspense fallback={<FuzionLoader sx={{ m: "auto" }}>Authenticating...</FuzionLoader>}>
            <ErrorBoundary fallback={<AuthFailed />}>
                <Content
                    query={query}
                    onLoad={data => updateState({
                        token: data.token,
                        userData: data.userData,
                        state: "authenticated"
                    })}
                    children={children}
                />
            </ErrorBoundary>
        </Suspense>
        <div
            style={{
                position: "absolute",
                right: 16,
                bottom: 8,
                zIndex: 1100,
                pointerEvents: "none",
            }}
        >
            <StatusLabel slotProps={{ text: { sx: { color: "grey.500" } } }} />
        </div>
    </AuthContext.Provider>;

    function updateState(newState: Partial<AuthContextState>) {
        setState((prevState) => ({ ...prevState, ...newState }));
    }
}

export function useAuthContext() {
    const context = useContext(AuthContext);
    return {
        state: {
            token: context.state.token,
            userData: context.state.userData,
        }
    };
}

type QueryResult = {
    token: string;
    userData?: PersonModel;
}
function Content({ children, query, onLoad }: PropsWithChildren<{
    query: Promise<QueryResult>;
    onLoad(data: QueryResult): void;
}>) {
    const result = use<QueryResult>(query);
    onLoad(result);
    return children;
}

function AuthFailed() {
    return <FuzionLoader sx={{ m: "auto" }}>
        <Alert severity="error" variant="outlined" icon={<ErrorIcon fontSize="large" />} sx={{ mt: 2, mx: "auto", p: 2, width: "fit-content", minWidth: 300 }}>
            <AlertTitle>Authentication Error</AlertTitle>
            <Text variant="body2" component="p">Please try again later.</Text>
        </Alert>
    </FuzionLoader>
}
